import orderBy from 'lodash/orderBy'
import { action, autorun, comparer, computed, makeObservable, observable } from 'mobx'
import { getValue, isPending } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'

import { CaseRecordType } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { arriere } from '@new/domains/fastBookingsTapeChart/stores/arriere'
import { fastBookingsTapeChartStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'
import { SearchCaseRowProps } from '@new/domains/operations/screens/CasesV2/CasesV2.types'
import {
  findCaseBooking,
  searchCases,
  SearchCasesProps,
} from '@new/domains/operations/screens/CasesV2/service/cases'
import { casesStore } from '@new/domains/operations/screens/CasesV2/stores/CasesStore'

import { getPropertyByIdTapeChartHeaderInfo } from '@arriere/modules/properties'
import { ROUTES } from '@routes'

import { CaseBookingProps, CaseTabs, HomeCasesProps } from './ArrivalsChecklist.types'
import { DueDatePeriodProps, formatDateRange, parseDates } from './ArrivalsChecklistSidePanel.utils'

export class ArrivalsChecklistStore {
  constructor() {
    makeObservable(this, {
      arrivalChecklistWorkOrderIds: observable,
      caseStatuses: observable,
      defaultCaseTab: observable,
      __ASYNC_homeCases: computed({ equals: comparer.structural }),
      __ASYNC_caseBookings: computed({ equals: comparer.structural }),
      __ASYNC_homeInfo: computed({ equals: comparer.structural }),
      isLoadingHomeCases: computed,
      isLoadingCaseBookings: computed,
      isLoadingHomeInfo: computed,
      homeCases: computed,
      caseBookings: computed,
      homeInfo: computed,
      setArrivalChecklistWorkOrderIds: action.bound,
      setDefaultCaseTab: action.bound,
      setSelectedArrivalChecklistHomeId: action.bound,
      setCaseStatuses: action.bound,
      setCasesDueDatePeriod: action.bound,
      setCleansAndInspectionsDueDatePeriod: action.bound,
    })

    autorun(() => {
      const selectedArrivalChecklistHomeId = this.selectedArrivalChecklistHomeId.get()

      if (selectedArrivalChecklistHomeId) {
        fastBookingsTapeChartStore.selectProperty(selectedArrivalChecklistHomeId)
      }
    })
  }

  arrivalChecklistWorkOrderIds?: string[]

  caseStatuses?: string[]

  defaultCaseTab?: CaseTabs

  casesDueDatePeriod = computedRouteParam<DueDatePeriodProps | undefined>('casesDueDatePeriod', {
    patterns: [ROUTES.fastTapeChart.path, ROUTES.hotelTapeChart.path],
    defaultValue: undefined,
    format: formatDateRange,
    parse: parseDates,
  })

  cleansAndInspectionsDueDatePeriod = computedRouteParam<DueDatePeriodProps | undefined>(
    'cleansAndInspectionsDueDatePeriod',
    {
      patterns: [ROUTES.fastTapeChart.path, ROUTES.hotelTapeChart.path],
      defaultValue: undefined,
      format: formatDateRange,
      parse: parseDates,
    },
  )

  selectedArrivalChecklistHomeId = computedRouteParam<string | undefined>(
    'selectedArrivalChecklistHomeId',
    {
      patterns: [ROUTES.fastTapeChart.path, ROUTES.hotelTapeChart.path],
      defaultValue: undefined,
    },
  )

  private async searchHomeCases(args: SearchCasesProps) {
    return await searchCases({ args }).then(response => {
      return response.values.results.map(aCase => ({
        ...aCase,
        home: { ...aCase.home, region: aCase.home.region ?? { id: '', name: '' } },
      }))
    })
  }

  private async getHomeWorkOrderIds(homeId: string) {
    return await arriere.fetchHomeCheckInStatus({ homeIds: [homeId] }).then(homes => {
      return homes.flatMap(({ home }) => {
        const combinedCases = [
          ...(home?.operationReadinessStatus?.cleanDetails?.cases ?? []),
          ...(home?.operationReadinessStatus?.inspectedDetails?.cases ?? []),
          ...(home?.operationReadinessStatus?.othersCasesDetails?.cases ?? []),
        ]

        return combinedCases?.map(({ workOrderId }) => workOrderId)
      })
    })
  }

  get __ASYNC_homeCases() {
    void [casesStore.refresh]

    const selectedCaseHomeId = this.selectedArrivalChecklistHomeId.get()
    if (selectedCaseHomeId) {
      const workOrderIds = this.arrivalChecklistWorkOrderIds
      const caseStatuses = this.caseStatuses

      const recordTypes = [
        CaseRecordType.housekeeping,
        CaseRecordType.inspection,
        CaseRecordType.fieldOps,
      ]

      const baseHomeCasesArgs = {
        statuses: caseStatuses,
        recordTypes,
      }

      if (workOrderIds && workOrderIds.length > 0) {
        const caseArgs = {
          ...baseHomeCasesArgs,
          workOrderIds,
          pagination: {
            page: 1,
            size: workOrderIds.length,
          },
        }
        return this.searchHomeCases(caseArgs)
      }

      return this.getHomeWorkOrderIds(selectedCaseHomeId).then(async resWorkOrderIds => {
        if (resWorkOrderIds && resWorkOrderIds.length > 0) {
          const caseArgs = {
            ...baseHomeCasesArgs,
            workOrderIds: resWorkOrderIds,
            pagination: {
              page: 1,
              size: resWorkOrderIds.length,
            },
          }

          return this.searchHomeCases(caseArgs)
        }

        return []
      })
    }

    return Promise.resolve([])
  }

  get __ASYNC_caseBookings() {
    const cases = this.homeCases

    if (cases && cases.fieldOps.length > 0) {
      return Promise.all(
        cases.fieldOps.map(fieldOpsCase => findCaseBooking(fieldOpsCase.workOrderId)),
      ).then(res => {
        const bookings = res.reduce((acc: CaseBookingProps[], currentBooking) => {
          if (currentBooking?.booking) {
            return [
              ...acc,
              {
                ...currentBooking.booking,
                workOrderId: currentBooking.workOrderId,
              },
            ]
          }

          return acc
        }, [])
        return bookings
      })
    }

    return Promise.resolve([])
  }

  get __ASYNC_homeInfo() {
    void [casesStore.refresh]

    const selectedCaseHomeId = this.selectedArrivalChecklistHomeId.get()
    if (selectedCaseHomeId) {
      return getPropertyByIdTapeChartHeaderInfo({
        homeId: selectedCaseHomeId,
      })
    }

    return Promise.resolve(undefined)
  }

  get isLoadingHomeCases() {
    return isPending(this.__ASYNC_homeCases)
  }

  get isLoadingCaseBookings() {
    return isPending(this.__ASYNC_caseBookings)
  }

  get isLoadingHomeInfo() {
    return isPending(this.__ASYNC_homeInfo)
  }

  get homeCases() {
    const cases = getValue(this.__ASYNC_homeCases)

    if (cases) {
      const sortedCases = orderBy(cases, 'dueDate', 'desc')

      const groupedCases = sortedCases?.reduce(
        (accCases: HomeCasesProps, currentCase: SearchCaseRowProps) => {
          switch (currentCase.recordType) {
            case CaseRecordType.housekeeping:
              return {
                ...accCases,
                cleans: [...accCases.cleans, currentCase],
              }
            case CaseRecordType.fieldOps:
              return {
                ...accCases,
                fieldOps: [...accCases.fieldOps, currentCase],
              }
            case CaseRecordType.inspection:
              return {
                ...accCases,
                inspections: [...accCases.inspections, currentCase],
              }
            default:
              return accCases
          }
        },
        {
          cleans: [],
          fieldOps: [],
          inspections: [],
        },
      )

      return groupedCases
    }

    return undefined
  }

  get caseBookings() {
    return getValue(this.__ASYNC_caseBookings)
  }

  get homeInfo() {
    return getValue(this.__ASYNC_homeInfo)
  }

  setArrivalChecklistWorkOrderIds(workOrderIds?: string[]) {
    this.arrivalChecklistWorkOrderIds = workOrderIds
  }

  setDefaultCaseTab(caseTab?: CaseTabs) {
    this.defaultCaseTab = caseTab
  }

  setSelectedArrivalChecklistHomeId(homeId?: string) {
    if (homeId) {
      fastBookingsTapeChartStore.selectProperty(homeId)
    }
    this.selectedArrivalChecklistHomeId.replace(homeId)
    this.caseStatuses = undefined
  }

  setCaseStatuses(caseStatuses?: string[]) {
    this.caseStatuses = caseStatuses
  }

  setCasesDueDatePeriod(dueDatePeriod?: DueDatePeriodProps) {
    this.casesDueDatePeriod.replace(dueDatePeriod)
  }

  setCleansAndInspectionsDueDatePeriod(dueDatePeriod?: DueDatePeriodProps) {
    this.cleansAndInspectionsDueDatePeriod.replace(dueDatePeriod)
  }
}

export const arrivalsChecklistStore = new ArrivalsChecklistStore()
